import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button 
            data-testid="back-btn"
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-[#00E0FF]" />
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              PRIVACY POLICY
            </h1>
          </div>
          <p className="text-gray-400">Last Updated: January 2026</p>
        </motion.div>

        <Card className="glass-effect">
          <CardContent className="p-8 space-y-6 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>1. INFORMATION WE COLLECT</h2>
              <p className="leading-relaxed mb-3">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Account Information:</strong> Name, email address, profile picture (via Google OAuth)</li>
                <li><strong>Payment Information:</strong> Payment details processed securely through Stripe (we do not store credit card information)</li>
                <li><strong>Usage Data:</strong> Predictions generated, portfolio data, alerts created</li>
                <li><strong>Device Information:</strong> IP address, browser type, device identifiers</li>
                <li><strong>Cookies:</strong> Session tokens and preferences</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>2. HOW WE USE YOUR INFORMATION</h2>
              <p className="leading-relaxed mb-3">
                We use the collected information to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and maintain the Service</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send AI predictions and price alerts</li>
                <li>Improve and personalize user experience</li>
                <li>Communicate updates, promotions, and service information</li>
                <li>Detect and prevent fraud and abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>3. INFORMATION SHARING</h2>
              <p className="leading-relaxed mb-3">
                We do NOT sell your personal information. We may share information with:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Service Providers:</strong> Stripe (payments), Google (authentication), hosting providers</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>4. DATA SECURITY</h2>
              <p className="leading-relaxed">
                We implement industry-standard security measures to protect your data, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                <li>Encrypted connections (HTTPS/TLS)</li>
                <li>Secure session management</li>
                <li>Regular security audits</li>
                <li>Access controls and authentication</li>
                <li>Encrypted data storage</li>
              </ul>
              <p className="leading-relaxed mt-3">
                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>5. YOUR RIGHTS</h2>
              <p className="leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your account</li>
                <li>Object to processing of your data</li>
                <li>Export your data</li>
                <li>Opt-out of marketing communications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>6. COOKIES</h2>
              <p className="leading-relaxed">
                We use cookies and similar technologies to maintain your session, remember preferences, and analyze usage. You can control cookies through your browser settings, but disabling them may affect functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>7. THIRD-PARTY SERVICES</h2>
              <p className="leading-relaxed mb-3">
                Our Service integrates with:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Google OAuth:</strong> For authentication (covered by Google's Privacy Policy)</li>
                <li><strong>Stripe:</strong> For payment processing (covered by Stripe's Privacy Policy)</li>
                <li><strong>Market Data APIs:</strong> Alpha Vantage, CoinGecko</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>8. CHILDREN'S PRIVACY</h2>
              <p className="leading-relaxed">
                The Service is not intended for users under 18 years of age. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>9. DATA RETENTION</h2>
              <p className="leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide services. You may request deletion at any time, subject to legal retention requirements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>10. CHANGES TO POLICY</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through the Service. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>11. CONTACT US</h2>
              <p className="leading-relaxed">
                For privacy-related questions or to exercise your rights, contact us at: <a href="mailto:privacy@tradeinsights.com" className="text-[#00E0FF] hover:underline">privacy@tradeinsights.com</a>
              </p>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
